import { FC, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { Card, CardContent, CardHeader, CardTitle } from '@oasisprotocol/ui-library/src/components/cards'
import { Link as RouterLink } from 'react-router-dom'
import { useGetConsensusValidators } from '../../../oasis-nexus/api'
import { Validators } from '../../components/Validators'
import { API_MAX_TOTAL_COUNT, NUMBER_OF_ITEMS_ON_DASHBOARD } from '../../../config'
import { ConsensusScope } from '../../../types/searchScope'
import { RouteUtils } from 'app/utils/route-utils'
import { CardHeaderWithCounter } from '../../components/CardHeaderWithCounter'
import { ErrorBoundary } from '../../components/ErrorBoundary'
import { Typography } from '@oasisprotocol/ui-library/src/components/typography'
import { Link } from '@oasisprotocol/ui-library/src/components/link'

const limit = NUMBER_OF_ITEMS_ON_DASHBOARD

/** Only entities currently in the consensus validator set (exclude observers / escrow-only). */
function useConsensusValidatorSet(network: ConsensusScope['network']) {
  const validatorsQuery = useGetConsensusValidators(network, { limit: API_MAX_TOTAL_COUNT })
  const inSet = useMemo(
    () => validatorsQuery.data?.data.validators.filter(v => v.in_validator_set) ?? [],
    [validatorsQuery.data?.data.validators],
  )
  return { validatorsQuery, inSet }
}

const ValidatorsTitle: FC<{ scope: ConsensusScope }> = ({ scope }) => {
  const { t } = useTranslation()
  const { inSet, validatorsQuery } = useConsensusValidatorSet(scope.network)

  return (
    <CardHeaderWithCounter
      label={t('validator.listTitle')}
      totalCount={validatorsQuery.isFetched ? inSet.length : undefined}
      isTotalCountClipped={false}
    />
  )
}

const ValidatorsContent: FC<{ scope: ConsensusScope }> = ({ scope }) => {
  const { validatorsQuery, inSet } = useConsensusValidatorSet(scope.network)

  return (
    <Validators
      validators={inSet.slice(0, limit)}
      stats={validatorsQuery.data?.data.stats}
      isLoading={validatorsQuery.isLoading}
      limit={limit}
      pagination={false}
    />
  )
}

export const ValidatorsCard: FC<{ scope: ConsensusScope }> = ({ scope }) => {
  const { t } = useTranslation()

  return (
    <Card variant="layout">
      <CardHeader>
        <CardTitle>
          <ErrorBoundary fallbackContent={t('validator.listTitle')}>
            <Typography variant="h3">
              <ValidatorsTitle scope={scope} />
            </Typography>
          </ErrorBoundary>

          <Link asChild textColor="primary" className="font-medium px-4">
            <RouterLink to={RouteUtils.getValidatorsRoute(scope.network)}>{t('common.viewAll')}</RouterLink>
          </Link>
        </CardTitle>
      </CardHeader>
      <CardContent>
        <ErrorBoundary light={true}>
          <ValidatorsContent scope={scope} />
        </ErrorBoundary>
      </CardContent>
    </Card>
  )
}
